import React, { useState, useEffect } from 'react';
import { X, Check, Sparkles, Loader2, Zap } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

const PricingModal = ({ isOpen, onClose, defaultPlan = 'creador' }) => {
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);
  const [billingInterval, setBillingInterval] = useState('yearly');
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPricing();
    }
  }, [isOpen]);

  const fetchPricing = async () => {
    try {
      const res = await api.get('/billing/stripe/pricing');
      setPricing(res.data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await api.post('/billing/stripe/create-checkout-session', {
        plan: selectedPlan,
        interval: billingInterval,
        origin_url: window.location.origin
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMsg = err.response?.data?.detail || 'Error al procesar el pago. Inténtalo de nuevo.';
      alert(errorMsg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  const isFounders = pricing?.is_founders;
  const creadorPrices = pricing?.plans?.creador?.prices || {};
  const agenciaPrices = pricing?.plans?.agencia?.prices || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl bg-[#12131E] rounded-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-5 border-b border-white/10">
          <button 
            onClick={onClose}
            className="absolute right-4 top-4 text-white/40 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            {isFounders && (
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full px-4 py-1.5 mb-3">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-semibold">Oferta Founders - Precio especial</span>
              </div>
            )}
            <h2 className="text-2xl font-bold text-white">Elige tu plan</h2>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#00E5A0] animate-spin" />
          </div>
        ) : (
          <div className="p-6">
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-[#00E5A0] text-black'
                    : 'bg-white/5 text-white/60 hover:text-white'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-4 py-2 rounded-lg font-medium transition-all relative ${
                  billingInterval === 'yearly'
                    ? 'bg-[#00E5A0] text-black'
                    : 'bg-white/5 text-white/60 hover:text-white'
                }`}
              >
                Anual
                <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  -17%
                </span>
              </button>
            </div>

            {/* Plans Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Plan Creador */}
              <div 
                onClick={() => setSelectedPlan('creador')}
                className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedPlan === 'creador'
                    ? 'border-[#00E5A0] bg-[#00E5A0]/5'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {selectedPlan === 'creador' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E5A0] text-black text-xs font-bold px-3 py-1 rounded-full">
                    Seleccionado
                  </div>
                )}
                
                <h3 className="text-xl font-bold text-white mb-2">Plan Creador</h3>
                <p className="text-white/60 text-sm mb-4">Para freelancers y creadores de contenido</p>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">
                      {creadorPrices[billingInterval]?.amount_display || '—'}
                    </span>
                    <span className="text-white/60">/{billingInterval === 'yearly' ? 'año' : 'mes'}</span>
                  </div>
                  {billingInterval === 'yearly' && (
                    <p className="text-[#00E5A0] text-sm mt-1">
                      {creadorPrices[billingInterval]?.monthly_equivalent}/mes
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {[
                    '500 créditos/mes',
                    isFounders && '+500 créditos bonus de Founder',
                    'Hasta 5 proyectos',
                    'Todas las integraciones',
                    'Soporte por email'
                  ].filter(Boolean).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/80 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 ${feature.includes('bonus') ? 'text-amber-400' : 'text-[#00E5A0]'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Plan Agencia */}
              <div 
                onClick={() => setSelectedPlan('agencia')}
                className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedPlan === 'agencia'
                    ? 'border-[#00E5A0] bg-[#00E5A0]/5'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {selectedPlan === 'agencia' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E5A0] text-black text-xs font-bold px-3 py-1 rounded-full">
                    Seleccionado
                  </div>
                )}
                
                <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Popular
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">Plan Agencia</h3>
                <p className="text-white/60 text-sm mb-4">Para agencias y equipos de marketing</p>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">
                      {agenciaPrices[billingInterval]?.amount_display || '—'}
                    </span>
                    <span className="text-white/60">/{billingInterval === 'yearly' ? 'año' : 'mes'}</span>
                  </div>
                  {billingInterval === 'yearly' && (
                    <p className="text-[#00E5A0] text-sm mt-1">
                      {agenciaPrices[billingInterval]?.monthly_equivalent}/mes
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {[
                    '2.000 créditos/mes',
                    isFounders && '+1.500 créditos bonus de Founder',
                    'Proyectos ilimitados',
                    'Todas las integraciones',
                    'Soporte prioritario',
                    'API access'
                  ].filter(Boolean).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/80 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 ${feature.includes('bonus') ? 'text-amber-400' : 'text-[#00E5A0]'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Checkout Button */}
            <div className="mt-8">
              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full h-14 bg-[#00E5A0] hover:bg-[#00E5A0]/90 text-black font-bold text-lg"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    Continuar con {selectedPlan === 'creador' ? 'Plan Creador' : 'Plan Agencia'}
                  </>
                )}
              </Button>
              
              <p className="text-center text-white/40 text-sm mt-4">
                Pago seguro con Stripe · Cancela cuando quieras
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingModal;
